import {useEffect, useMemo, useState} from 'react'
import {BehaviorSubject, of, timer} from 'rxjs'
import {CFModel, CFValue} from 'module/CustomField/type/CFType'
import {filter, mapTo, mergeMap, switchMap, tap} from 'rxjs/operators'
import {CFComponentProps} from 'module/CustomField/type/CFComponentType'
import {CustomFieldTO} from 'generated/JavaTO'
import {CF} from 'module/CustomField/util/CustomField'
import _ from 'lodash'
import {DqElement, isDqSearchable, searchDqWithCFDef} from 'module/Dq/searchDq'

type DqSearchResult = DqElement[]

function mapResult(x: DqElement, cfDef: CustomFieldTO, valueBeforeLabel?: boolean) {
	let value = CF.transformForView(cfDef, x.value);
	return {
		...x,
		value,
		label: [valueBeforeLabel === false ? '' : value, x.label].filter(le => _.trim(le as string)).join(' - '),
	}
}

interface DqSearchRequest {
	searchTerm: CFValue
	model: CFModel
	onlyLabelCache?: boolean // use for non-view-initiated searches
}

interface DqSearchResponse {
	request: DqSearchRequest
	result: DqSearchResult
	labelCache: LabelCache
	searchable: boolean
}


type LabelCache = { [id in string]: string }

export function useDqSearchRx({cfDef, model, pos, isAze, valueBeforeLabel}: Pick<CFComponentProps<CFValue>, 'cfDef' | 'model' | 'pos' | 'isAze'> & { valueBeforeLabel?: boolean }) {
	const [labelCache, setLabelCache] = useState<LabelCache>({})
	const [suggestions, setSuggestions] = useState<DqSearchResult>([])
	const [loading, setLoading] = useState(false)
	const {searchSubject, responseSubject} = useMemo(() => ({
		searchSubject: new BehaviorSubject<DqSearchRequest>({searchTerm: '', model}),
		responseSubject: new BehaviorSubject<null | DqSearchResponse>(null),
	}), []);
	useEffect(() => {
		const subscription = searchSubject.pipe(
			filter(() => Boolean(cfDef.dqKey)),
			switchMap((req) => {
				if (req.onlyLabelCache) return of(req)
				else return timer(100).pipe(mapTo(req))
			}),
			tap(() => setLoading(true)),
			mergeMap((request) => {
				const {searchTerm, model} = request
				return searchDqWithCFDef(cfDef, model, pos, searchTerm as string, isAze)
					.then((result) => ({result, request}))
			}),
			tap((ret) => {
				setLoading(false)
				const dqResult = ret.result.map(m => mapResult(m, cfDef, valueBeforeLabel))
				setLabelCache(lc => {
					lc = dqResult.reduce((prev, curr) => {
						prev[curr.value as string] = (curr.label || curr.value) as string
						return prev
					}, {...lc})
					responseSubject.next({
						...ret,
						labelCache: lc,
						searchable: isDqSearchable(cfDef.dqKey),
					})
					return lc
				})
				if (!ret.request.onlyLabelCache) setSuggestions(dqResult)
			}),
		).subscribe();
		return () => subscription.unsubscribe()
	}, [cfDef, searchSubject, responseSubject])
	return {
		searchSubject,
		suggestions,
		labelCache,
		loading,
		responseSubject,
	}
}